import { Link } from "react-router-dom"
import { Menu, ExternalLink } from "lucide-react"
import { useTranslation } from "react-i18next"
import { Button } from "@/components/ui/button"
import { Logo } from "@/components/common/Logo"
import { LanguageSwitcher } from "@/components/common/LanguageSwitcher"
import { UserMenu } from "@/features/auth/components/UserMenu"
import { useAuthMe } from "@/hooks/useAuthMe"
import { cn } from "@/lib/utils"

interface MobileHeaderProps {
  onMenuClick?: () => void
  className?: string
  showUserMenu?: boolean
  showMenuButton?: boolean
}

export function MobileHeader({
  onMenuClick,
  className,
  showUserMenu = true,
  showMenuButton = true,
}: MobileHeaderProps) {
  const { t } = useTranslation()

  return (
    <header
      className={cn(
        "h-14 px-4 shrink-0 bg-white border-b border-slate-200 flex items-center justify-between gap-2 safe-area-top",
        className
      )}
    >
      {/* Left: Hamburger Menu + Logo (not centred: on phones a centred logo runs into the right-hand links) */}
      <div className="flex items-center gap-1 min-w-0">
        {showMenuButton && onMenuClick && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onMenuClick}
            className="w-10 h-10 shrink-0 -ml-2"
            aria-label={t('header.openMenu')}
          >
            <Menu className="w-5 h-5" />
          </Button>
        )}
        <Link to="/" className="min-w-0">
          <Logo size="sm" />
        </Link>
      </div>

      {/* Right: Wrks AI Link + Language Switcher + User Menu */}
      <div className="flex items-center gap-2 shrink-0">
        {showUserMenu && <WrksLink />}
        <LanguageSwitcher />
        {showUserMenu && <UserMenu />}
      </div>
    </header>
  )
}

export function WrksLink() {
  const { t } = useTranslation()
  const { data: authMe } = useAuthMe()
  const href = authMe?.source === 'gov'
    ? 'https://gov.wrks.ai'
    : 'https://wrks.ai'

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-slate-500 hover:text-slate-700 rounded-md hover:bg-slate-100 transition-colors"
    >
      <span>{t('header.wrksAi')}</span>
      <ExternalLink className="hidden sm:block w-3 h-3" />
    </a>
  )
}
